/**
 * Persistent banner across the entire app while a founder is
 * impersonating a school-admin (ADR-073). Shows the impersonation
 * target, write-mode status, a stop form, and a write-mode link.
 *
 * Red border + ⚠ icon when write-mode is active so destructive actions
 * are visually distinct from the read-only default.
 */

import { Form, Link } from 'react-router'

export type ImpersonationBannerData = {
	impersonatedUserName: string
	startedAt: string
	writeModeUntil: string | null
	stopAction: string
}

export function ImpersonationBanner({
	data,
}: {
	data: ImpersonationBannerData
}) {
	const writeMode = data.writeModeUntil
		? new Date(data.writeModeUntil).getTime() > Date.now()
		: false

	return (
		<div
			role="status"
			aria-live="polite"
			className={`sticky top-0 z-[60] flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm ${
				writeMode
					? 'border-red-500/40 bg-red-500/10 text-red-100'
					: 'border-orange-500/40 bg-orange-500/10 text-orange-100'
			}`}
		>
			<span aria-hidden="true">{writeMode ? '⚠' : '◎'}</span>
			<span className="font-medium">
				{writeMode ? 'Write-mode actief' : 'Impersonatie actief (read-only)'}
			</span>
			<span className="text-xs opacity-80">
				als {data.impersonatedUserName}
			</span>
			{writeMode && data.writeModeUntil ? (
				<span className="text-xs opacity-80">
					tot{' '}
					{new Date(data.writeModeUntil).toLocaleTimeString('nl-NL', {
						hour: '2-digit',
						minute: '2-digit',
					})}
				</span>
			) : null}

			<div className="ml-auto flex items-center gap-2">
				{!writeMode ? (
					<Link
						to="/founders/impersonation/write-mode"
						className="rounded-md border border-current px-2 py-1 text-xs font-medium hover:bg-white/10"
					>
						Activeer write-mode
					</Link>
				) : null}
				<Form method="POST" action={data.stopAction}>
					<input type="hidden" name="intent" value="stop" />
					<button
						type="submit"
						className="rounded-md bg-white/10 px-2 py-1 text-xs font-medium text-white hover:bg-white/20"
					>
						Stop impersonatie
					</button>
				</Form>
			</div>
		</div>
	)
}
